import React, { useRef } from 'react';
import { observer } from 'mobx-react';
import { useDialog } from '@shared-ui/dialog-context';
import { useLocalization } from '@shared-ui/localization-context';

// Uitk components
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkPriceLockup } from 'uitk-react-price-lockup';
import { UitkText } from 'uitk-react-text';

// GraphQL
import { CampaignRecommendationsWithOffersQuery } from "src/common/__generated__/api/types";
import Offers = CampaignRecommendationsWithOffersQuery.Offers;

// Support
import { TrackedLink } from 'src/components/common/modules/analytics/tracked-link';
import { Action } from 'src/components/common/modules/analytics/analytics-utils';
import { InfoDialog } from 'src/components/common/modules/info-dialog/info-dialog';
import { checkValue } from 'src/components/common/helper';

interface PriceComponentProps {
  offer: Offers;
  cardIndex: number;
  campaignId: string;
  moduleName: string;
  cardClicked: () => void;
}

const PricePropertyComponent = observer(
  ({ offer, cardIndex, campaignId, moduleName, cardClicked }: PriceComponentProps) => {
    const offering = offer as any;
    const { formatText } = useLocalization();
    const [isDialogOpen, dialogActions, PriceDialogComponent] = useDialog(`disclaimer-info-${campaignId}-${cardIndex}`);
    const triggerRef = useRef();
    const handleBodyClick = (e) => {
      document.body.removeEventListener('click', handleBodyClick, true);
      if (e.target.classList.contains('layer-overlay-active')) {
        domManipulationWhenClose();
      }
    };

    const openDialog = () => {
      document.body.addEventListener('click', handleBodyClick, true);
      domManipulationWhenOpen();
      setTimeout(() => {
        dialogActions.openDialog();
      }, 200);
    };

    const closeDialog = () => {
      document.body.removeEventListener('click', handleBodyClick, true);
      dialogActions.closeDialog();
      domManipulationWhenClose();
    };

    const domManipulationWhenClose = () => {
      setTimeout(() => {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }, 300);
    };

    const domManipulationWhenOpen = () => {
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    };

    if (offering?.mod?.modSignInText) {
      return (
        <TrackedLink
          moduleName={moduleName}
          action={Action.CLICK}
          rfrr={`football-card-${campaignId}-${cardIndex}`}
          onClick={cardClicked}
          href={offering?.mod?.signInUrl?.relativePath}
          target={'_blank'}
        >
          <UitkText size={300} weight="bold" align="right">
            {offering?.mod?.modSignInText}
          </UitkText>
        </TrackedLink>
      );
    }

    // Check the total price message
    const totalPriceMesasge = checkValue(offering?.displayMessages, true)
      ? offering?.displayMessages.filter((item) => item?.lineItems?.[0]?.state === 'BREAKOUT_TYPE_SECONDARY_PRICE')?.[0]
        ?.lineItems?.[0]?.value
      : '';
    const priceSubtextFirst = checkValue(totalPriceMesasge) ? totalPriceMesasge + ' | ' + offering?.roomNightMessage : offering?.roomNightMessage;

    // Check the legal info
    const legalInfo = checkValue(offering?.displayMessages, true)
      ? offering?.displayMessages.filter(
        (item) => item?.lineItems?.[0]?.state === 'BREAKOUT_TYPE_TAX_AND_FEE_CLARIFY'
      )?.[0]?.lineItems?.[0]?.value
      : '';

    let priceSubtextStandard = checkValue(legalInfo) ? [legalInfo] : [];

    return (
      <>
        <UitkPriceLockup
          lockupPrice={offering?.lead?.formatted}
          priceSubtextFirst={`${priceSubtextFirst}`}
          a11yPrice={
            offering?.displayMessages?.[0]?.lineItems?.[0]?.price?.accessibilityLabel + 
              priceSubtextFirst + 
              priceSubtextStandard || ''}
          a11yStrikeThroughDialogTrigger={'priceDisclaimerLabel'}
          strikeThroughPrice={offering?.strikeOut?.formatted}
          priceSubtextStandard={`${priceSubtextStandard}`}
          {...(checkValue(offering?.disclaimer?.value) ? {
            strikeThroughTrigger:
              <button className="disclaimerInfoIcon" onClick={openDialog} type="button">
                <UitkIcon
                  name="info_outline"
                  size={UitkIconSize.SMALL}
                  id={`disclaimer-info-${campaignId}-${cardIndex}`}
                  title={formatText('merchCampaign.disclaimerIcon.title')}
                />
              </button>
          } : {})}
        />
        <PriceDialogComponent>
          <InfoDialog
            infoText={offering?.disclaimer?.value}
            closeDialog={closeDialog}
            buttonText={'Close'}
            isDialogOpen={isDialogOpen}
            triggerRef={triggerRef}
          />
        </PriceDialogComponent>
      </>
    );
  }
);

export { PricePropertyComponent, PriceComponentProps }
